import { useMemo, useState, useCallback } from "react";
import {
  EMAIL_TO_FIELD_SEPARATOR,
  formatContactsEmailToField,
  type EmailToFieldContact,
  type EmailToFieldEntry,
} from "@domaddress/shared";

interface Props {
  /** Geselecteerde contacten (of groepsleden). */
  contacts?: EmailToFieldContact[];
  /** Met per persoon gekozen e-mail (zend-contacten). */
  entries?: EmailToFieldEntry[];
}

/**
 * Toont een gegenereerde Aan:-regel en kopieert die naar het klembord
 * (formaat: Naam &lt;email@domein.nl&gt;, gescheiden door puntkomma’s).
 */
export default function EmailToRecipientsCopy({ contacts, entries }: Props) {
  const [copied, setCopied] = useState(false);
  const rows = useMemo(
    (): EmailToFieldEntry[] =>
      entries ?? (contacts ?? []).map((c) => ({ ...c })),
    [contacts, entries]
  );
  const { value, included, omitted } = useMemo(() => formatContactsEmailToField(rows), [rows]);

  const onCopy = useCallback(async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      window.alert("Kopiëren lukt niet in deze browser. Selecteer de tekst handmatig.");
    }
  }, [value]);

  const rowCount = value ? value.split(EMAIL_TO_FIELD_SEPARATOR).length + 1 : 3;

  return (
    <div className="form-group email-to-recipients-block">
      <label htmlFor="email-to-field-generated">Aan: (voor uw e-mailprogramma)</label>
      <p className="page-subtitle" style={{ marginTop: 0, marginBottom: "0.5rem" }}>
        Formaat: <em>Naam &lt;e-mail&gt;</em>, gescheiden door puntkomma&apos;s. Plak in het Aan-veld van Outlook,
        Gmail of Apple Mail.
      </p>
      {included === 0 ? (
        <p className="form-error" style={{ marginTop: 0 }}>
          Geen van de geselecteerde personen heeft een e-mailadres in het adresboek.
        </p>
      ) : (
        <>
          <textarea
            id="email-to-field-generated"
            className="search-input email-to-field-textarea"
            readOnly
            rows={Math.min(8, Math.max(3, rowCount))}
            value={value}
            aria-label="Gegenereerde Aan-regel"
          />
          {omitted > 0 && (
            <p className="page-subtitle" style={{ marginTop: "0.35rem" }}>
              {omitted} {omitted === 1 ? "persoon" : "personen"} zonder e-mail overgeslagen.
            </p>
          )}
          <button type="button" className="btn-secondary" onClick={() => void onCopy()}>
            {copied ? "Gekopieerd!" : "Kopieer Aan-veld"}
          </button>
        </>
      )}
    </div>
  );
}
